import { sessionRejected, retryableRead } from "../auth/session_policy.js";
import axios from "axios";

/**
 * Axios instance used by every API call from the HQ SPA.
 *
 * - `baseURL` is "" by default so calls hit `/api/...` on the same origin
 *   (proxied by Vite in dev, by CloudFront in prod). Override with
 *   VITE_API_BASE_URL=https://hq-api.scheduledrop.com if hosting cross-origin.
 * - `withCredentials: true` so the httpOnly access/refresh cookies set by
 *   /api/auth/login are sent on every request (and read on /me, /refresh_token).
 * - Single in-flight refresh: a 401 triggers ONE refresh attempt; any other
 *   401s during that window queue and replay against the refreshed session.
 *   Prevents a thundering herd of refreshes when the access cookie expires
 *   mid-page-load.
 * - If the refresh itself fails (refresh cookie expired/revoked), we notify the
 *   registered session-expired handler so the auth layer can drop to unauthed
 *   and bounce the user to /login instead of stranding them on a dead page.
 */
const apiClient = axios.create({
  baseURL: import.meta.env?.VITE_API_BASE_URL || "",
  withCredentials: true,
});

// Bridge from this non-React module to AuthProvider. AuthProvider registers a
// callback on mount; the interceptor invokes it when a refresh fails so React
// state (and routing) react to the now-dead session.
let sessionExpiredHandler = null;

export function setSessionExpiredHandler(handler) {
  sessionExpiredHandler = handler;
}

let refreshPromise = null;

function refreshTokens() {
  if (!refreshPromise) {
    refreshPromise = apiClient
      .post("/api/auth/refresh_token")
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config || {};
    // GETs are safe to repeat; mutations, login and OTP requests are never replayed.
    if (retryableRead(error) && (original._connectionRetries || 0) < 2) {
      original._connectionRetries = (original._connectionRetries || 0) + 1;
      await new Promise((resolve) => setTimeout(resolve, 750 * original._connectionRetries));
      return apiClient(original);
    }
    const status = error.response?.status;
    const url = original.url || "";

    // verify_otp/resend_otp 401s mean "wrong code" or "challenge gone", not an
    // expired session — refreshing on them would be meaningless.
    const isAuthFlow =
      url.endsWith("/api/auth/login") ||
      url.endsWith("/api/auth/verify_otp") ||
      url.endsWith("/api/auth/resend_otp") ||
      url.endsWith("/api/auth/refresh_token") ||
      url.endsWith("/api/auth/logout");

    if (status === 401 && !original._retried && !isAuthFlow) {
      original._retried = true;
      try {
        await refreshTokens();
        return apiClient(original);
      } catch (refreshErr) {
        // A temporary server/network failure does not invalidate the session.
        if (sessionRejected(refreshErr) && sessionExpiredHandler) sessionExpiredHandler();
        return Promise.reject(refreshErr);
      }
    }
    return Promise.reject(error);
  },
);

export default apiClient;
